/**
 * Donor Import — a Kindful transaction report in, a donor registry out.
 *
 * Two steps, the same shape as Import Units: choosing a file asks the Worker what
 * it would do, and nothing is written until Import is pressed. The plan is worth reading
 * here more than on the registry import, because a giving report holds a great deal this
 * archive does not want — amounts, dates, and several hundred rows of campaigns that are
 * pools rather than records — and the plan is where that is accounted for.
 *
 * The unmatched list got shorter without a line changing here. It used to be read as
 * "campaigns that borrow the S4CA numbering — training, relief, country general"; since
 * migration 0031 the registry actually holds the training schools and the projects, so
 * those campaigns now match a record and link to it. What is left over is the genuine
 * remainder — split-gift pools, meeting fees, relief and country-general funds — which is
 * what that list was always meant to be.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, reportClientError } from '../lib/api'
import { Card, Tile, Badge, Notice, Spinner, Tabs, useToast, fmtInt } from '../components/ui'
import { useCode } from '../lib/terms'
import { Dropzone } from '../components/Dropzone'

const VERDICT = {
  create: { tone: 'ok', label: 'new donor' },
  update: { tone: 'warn', label: 'updated' },
  unchanged: { tone: 'muted', label: 'unchanged' },
}

/** A donor's name for the table, falling back to what identifies them. */
const donorLabel = (d) => d.preferred_name || d.email || '—'

export default function SettingsDonorImport({ user }) {
  const code = useCode()
  const toast = useToast()
  const qc = useQueryClient()

  const [file, setFile] = useState(null)
  const [plan, setPlan] = useState(null)
  const [planning, setPlanning] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [tab, setTab] = useState('changing')

  if (!user.caps.manageSettings) {
    return (
      <PageHeader title="Import Donors">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          A donor&rsquo;s name, address and the records they support is the most personal
          data in the archive, so both this import and the register it fills are kept to
          owners and administrators. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const reset = () => {
    setFile(null)
    setPlan(null)
    setOutcome(null)
    setError(null)
    setTab('changing')
  }

  /** Hand the file to the Worker and ask what it would do with it. */
  const choose = async (picked) => {
    const chosen = [...(picked ?? [])].find((f) => f && !f.name.startsWith('.'))
    setOutcome(null)
    setError(null)
    setPlan(null)
    setFile(chosen ?? null)
    if (!chosen) {
      setError('No file was chosen.')
      return
    }
    setPlanning(true)
    try {
      const fd = new FormData()
      fd.append('file', chosen)
      setPlan(await api('donor-registry/import/plan', { method: 'POST', formData: fd }))
    } catch (err) {
      setError(err.message)
      reportClientError('donor-import-plan', err, { filename: chosen.name, bytes: chosen.size })
    } finally {
      setPlanning(false)
    }
  }

  /** Send the same file again, this time to be written. */
  const run = async () => {
    setRunning(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await api('donor-registry/import', { method: 'POST', formData: fd })
      setOutcome(res)
      qc.invalidateQueries()
      toast.ok(`${fmtInt(res.created + res.updated)} donor${res.created + res.updated === 1 ? '' : 's'} written`)
    } catch (err) {
      setError(err.message)
      reportClientError('donor-import', err, { filename: file?.name, bytes: file?.size })
    } finally {
      setRunning(false)
    }
  }

  const s = plan?.summary
  const rows = plan?.rows ?? []
  const shown = tab === 'all' ? rows : tab === 'changing' ? rows.filter((r) => r.verdict !== 'unchanged') : []

  return (
    <PageHeader
      title="Import Donors"
      sub="A Kindful transaction report: one row per gift, with a header naming its columns"
      actions={
        plan && !running ? (
          <button className="btn" onClick={reset}>
            Choose another file
          </button>
        ) : null
      }
    >
      <div className="col">
        <Notice icon="info" title="How the file is read">
          <p>
            A transaction report exported from Kindful, with its own header line naming the
            columns — <strong>Preferred Name</strong>, <strong>Email</strong>,{' '}
            <strong>Activity Type</strong>, <strong>Created At</strong>,{' '}
            <strong>Amount</strong>, <strong>Campaigns</strong>. The columns are found by
            those names rather than by position, so a report that puts them in a different
            order still reads correctly; one whose header cannot be recognized is refused
            rather than guessed at.
          </p>
          <p>
            Three of those columns are read and then discarded. <strong>Amount</strong> is
            never stored — the registry records who supports a unit, not how much they gave.{' '}
            <strong>Created At</strong> decides which of a donor&rsquo;s names is the current one
            and is not stored either. <strong>Activity Type</strong> is only counted, so the
            plan can show you the mix of gifts, splits and recurring notices it read.
          </p>
          <p>
            A donor is identified by <strong>email address</strong>, lowercased: a donor who
            has given twenty times is one record, which is the merge you asked for. Where a
            row has no address the <strong>preferred name</strong> identifies it instead — the
            real report has several dozen such donors, and dropping them would quietly lose
            their giving. A row with neither is counted and left unattributed.
          </p>
          <p>
            A campaign becomes a link to a record when it begins with an{' '}
            <strong>S4CA code that the archive has a record for</strong> — a{' '}
            <strong>partner unit, training school or project</strong> alike, since all three
            share the one numbering and the one registry. A campaign for a training school
            therefore links to it like any other, provided the registry import has been run
            with the file&rsquo;s Fund Type column.
          </p>
          <p>
            Everything else — split-gift pools, meeting fees, and the relief and
            country-general funds that borrow the same numbering — is listed below by name
            and row count rather than dropped in silence, because a code with no record may
            equally mean a record missing from the archive.
          </p>
        </Notice>

        {!plan && (
          <Dropzone
            onFiles={choose}
            accept=".csv,text/csv,text/plain"
            busy={planning}
            busyLabel="Reading the report…"
            icon="file"
            title="Choose the transaction report"
          >
            Drop the .csv here, or click to pick it. Nothing is written until you have read the
            plan.
          </Dropzone>
        )}

        {error && (
          <Notice tone="danger" icon="warning" title="That file could not be read">
            {error}
          </Notice>
        )}

        {s && (
          <>
            <Card
              title={`${file?.name ?? 'The Report'} — What This Would Do`}
              sub={plan.filename ? `${fmtInt(s.file_rows)} rows read` : null}
            >
              <div className="col">
                {plan.messages.map((msg, i) => (
                  <p key={i} className="small" style={{ margin: 0 }}>
                    {msg}
                  </p>
                ))}
              </div>
            </Card>

            <div className="grid grid-tiles">
              <Tile label="Donors in this file" value={fmtInt(s.donors)} />
              <Tile label="New to the registry" value={fmtInt(s.create)} tone={s.create ? 'ok' : undefined} />
              <Tile label="Gaining a campaign" value={fmtInt(s.update)} tone={s.update ? 'warn' : undefined} />
              <Tile label="Already recorded" value={fmtInt(s.unchanged)} />
              <Tile label="Campaign links" value={fmtInt(s.links)} />
              <Tile label="New links" value={fmtInt(s.new_links)} tone={s.new_links ? 'ok' : undefined} />
              <Tile label="Partner units supported" value={`${fmtInt(s.units_touched)} of ${fmtInt(s.units_total)}`} />
              <Tile label="Identified by name only" value={fmtInt(s.by_name)} />
            </div>

            {/* Every row of the file, accounted for. The point of showing this is that the
                three discarded buckets add up with the used rows to the file's own total —
                so nothing has gone missing without being counted. */}
            <Card
              title="Every Row of the File, Accounted For"
              sub={`${fmtInt(s.rows_used)} used + ${fmtInt(
                s.skipped_not_a_unit + s.skipped_no_campaign + s.skipped_no_donor
              )} set aside = ${fmtInt(s.file_rows)}`}
            >
              <div className="table-wrap">
                <table className="data compact">
                  <tbody>
                    <tr>
                      <td className="small">Named a donor and a record</td>
                      <td className="num">{fmtInt(s.rows_used)}</td>
                      <td className="small subtle">merged into {fmtInt(s.donors)} donors</td>
                    </tr>
                    <tr>
                      <td className="small">Named a campaign that is not a record</td>
                      <td className="num">{fmtInt(s.skipped_not_a_unit)}</td>
                      <td className="small subtle">{s.unmatched.length} distinct campaigns, listed below</td>
                    </tr>
                    <tr>
                      <td className="small">Named no campaign at all</td>
                      <td className="num">{fmtInt(s.skipped_no_campaign)}</td>
                      <td className="small subtle">mailings, notes and recurring-plan notices</td>
                    </tr>
                    <tr>
                      <td className="small">Identified neither an address nor a name</td>
                      <td className="num">{fmtInt(s.skipped_no_donor)}</td>
                      <td className="small subtle">
                        {s.skipped_no_donor
                          ? 'cannot be attributed to a donor — some of these are gifts to real units'
                          : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            {!!s.unmatched.length && (
              <Card
                title="Campaigns That Are Not Records in the Registry"
                sub="Read, counted and not linked. Anything here that is really a missing unit should be added to the archive, then this file imported again."
              >
                <div className="table-wrap">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Code</th>
                        <th className="right">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.unmatched.map((u) => (
                        <tr key={u.campaign}>
                          <td className="small">{u.campaign}</td>
                          <td className="small mono subtle">{u.code ?? '—'}</td>
                          <td className="num">{fmtInt(u.rows)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {!!s.activity.length && (
              <Card title="What Kind of Rows These Were" sub="Counted only — the activity type is not stored">
                <div className="row wrap" style={{ gap: 8 }}>
                  {s.activity.map((a) => (
                    <Badge key={a.kind} tone="muted">
                      {a.kind} · {fmtInt(a.rows)}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            {!outcome && (
              <Card
                title="Write This to the Registry"
                sub={
                  s.create + s.update
                    ? 'Donors and campaign links are added and updated; nothing is removed.'
                    : 'The registry already holds every donor and campaign in this file.'
                }
              >
                <div className="row">
                  <button
                    className="btn btn-primary"
                    onClick={run}
                    disabled={running || !(s.create + s.update)}
                  >
                    {running
                      ? 'Importing…'
                      : s.create + s.update
                        ? `Import ${fmtInt(s.create + s.update)} donor${s.create + s.update === 1 ? '' : 's'}`
                        : 'Nothing to import'}
                  </button>
                  <button className="btn" onClick={reset} disabled={running}>
                    Cancel
                  </button>
                </div>
              </Card>
            )}

            {running && (
              <div className="row" style={{ marginBottom: 12 }}>
                <Spinner />
                <span className="small">Writing the registry…</span>
              </div>
            )}

            {outcome && (
              <Notice tone="ok" icon="check" title="Imported">
                {fmtInt(outcome.created)} donors added, {fmtInt(outcome.updated)} updated,{' '}
                {fmtInt(outcome.unchanged)} already matched the file. {fmtInt(outcome.new_links)} new
                campaign links across {fmtInt(outcome.units_touched)} records.
              </Notice>
            )}

            <Card title={`Donors in This File (${fmtInt(rows.length)})`}>
              <Tabs
                active={tab}
                onChange={setTab}
                tabs={[
                  { key: 'changing', label: `Changing (${fmtInt(s.create + s.update)})` },
                  { key: 'all', label: `All (${fmtInt(rows.length)})` },
                ]}
              />
              <div className="table-wrap">
                <table className="data compact">
                  <thead>
                    <tr>
                      <th>Donor</th>
                      <th>Identified by</th>
                      <th className="right">Gifts</th>
                      <th>Partner units supported</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.slice(0, 400).map((r) => (
                      <tr key={r.key}>
                        <td className="small">
                          {donorLabel(r)}
                          {r.contested_names && (
                            <div className="small subtle">
                              also named {r.contested_names.filter((n) => n !== r.preferred_name).join(', ')}
                            </div>
                          )}
                          {r.renaming && (
                            <div className="small subtle">
                              renamed from {r.renaming.from || '—'}
                            </div>
                          )}
                        </td>
                        <td className="small subtle">
                          {r.by_name ? <Badge tone="warn">name only</Badge> : r.email}
                        </td>
                        <td className="num">{fmtInt(r.report_rows)}</td>
                        <td className="small">
                          <div className="row wrap" style={{ gap: 4 }}>
                            {r.units.map((u) => (
                              <Badge key={u.partner_id} tone={u.is_new ? 'ok' : 'muted'} title={u.campaign}>
                                {code(u.s4ca_code)}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td>
                          <Badge tone={VERDICT[r.verdict].tone}>{VERDICT[r.verdict].label}</Badge>
                        </td>
                      </tr>
                    ))}
                    {!shown.length && (
                      <tr>
                        <td colSpan={5} className="small subtle">
                          Nothing in this view.
                        </td>
                      </tr>
                    )}
                    {shown.length > 400 && (
                      <tr>
                        <td colSpan={5} className="small subtle">
                          {fmtInt(shown.length - 400)} more not shown — all of them are imported.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>
    </PageHeader>
  )
}
